import WidgetConfig from "@/config/widget";
import AppConstants from "@/constants/AppConstants";
import { FORM_WIDGET_TAGS } from "./FormField.utils";

const PROBE_TAG = FORM_WIDGET_TAGS[AppConstants.ELEMENT_TYPE_FORM_INPUT];

const WIDGETS = ["input", "select", "radio", "checkbox"];

// Bare specifiers the widgets import, all mapped to one bundled Lit as protorenderer's pages do.
const LIT_RUNTIME = "lit-runtime.js";
const LIT_SPECIFIERS = [
  "lit",
  "lit/directives/class-map.js",
  "lit/directives/ref.js",
  "lit/directives/repeat.js",
  "lit/directives/style-map.js",
];

// Imported by every widget; preloaded with them so the import chain downloads in parallel.
const SHARED_MODULES = [LIT_RUNTIME, "lightBase.js"];

/**
 * Not part of the widgets: fill the field's box and stay a picture, not a live control.
 * TEMPORARY: stage dates are never filled; show mm/dd/yyyy as a placeholder until fixed upstream.
 */
const STAGE_CSS = `
:where(shwt-widget-form-input, shwt-widget-form-checkbox, shwt-widget-form-radio, shwt-widget-form-select) {
  height: 100%;
  pointer-events: none;
}

shwt-widget-form-input input[type="date"] {
  color: var(--_placeholderColor);
}
`;

/** @type {WeakMap<Document, Promise<void>>} */
const loadedDocuments = new WeakMap();

/**
 * @param {Document} doc
 * @param {string} tagName
 * @param {Object<string, string>} attributes
 * @returns {HTMLElement}
 */
const appendToHead = (doc, tagName, attributes) => {
  const node = doc.createElement(tagName);
  Object.assign(node, attributes);
  doc.head.appendChild(node);
  return node;
};

/**
 * Loads protorenderer's form widgets into the document a field renders in: custom elements
 * register per window, and the stage's iframe (`MountFrame`) never sees the app's own imports.
 * The import map has to land before the first module script, so a frame is set up once.
 * @param {Document | undefined} doc - The document the widget host lives in.
 * @returns {Promise<void> | undefined} Settles once the widgets are defined, for callers that wait.
 */
export const ensureFormWidgets = (doc) => {
  const registry = doc?.defaultView?.customElements;
  if (!registry) return undefined;
  if (loadedDocuments.has(doc)) return loadedDocuments.get(doc);
  if (registry.get(PROBE_TAG)) return Promise.resolve();

  const baseURL = WidgetConfig.protorenderer.widgetsURL;

  appendToHead(doc, "script", {
    type: "importmap",
    textContent: JSON.stringify({
      imports: Object.fromEntries(
        LIT_SPECIFIERS.map((specifier) => [specifier, baseURL + LIT_RUNTIME]),
      ),
    }),
  });
  [...SHARED_MODULES, ...WIDGETS.map((name) => `${name}.js`)].forEach((file) => {
    appendToHead(doc, "link", { rel: "modulepreload", href: baseURL + file });
  });
  ["formField", ...WIDGETS].forEach((name) => {
    appendToHead(doc, "link", { rel: "stylesheet", href: `${baseURL}${name}.css` });
  });
  appendToHead(doc, "style", { textContent: STAGE_CSS });

  // Not retried: the frame's module map keeps a failed fetch, so only a reload refetches it.
  const loaded = Promise.all(
    WIDGETS.map((name) => {
      const script = appendToHead(doc, "script", { type: "module", src: `${baseURL}${name}.js` });
      return new Promise((resolve, reject) => {
        script.addEventListener("load", resolve, { once: true });
        script.addEventListener("error", () => reject(new Error(`Failed to load ${script.src}`)), {
          once: true,
        });
      });
    }),
  ).then(() => {}, console.warn);
  loadedDocuments.set(doc, loaded);
  return loaded;
};
